import React, { SyntheticEvent } from 'react';
import FormControl from '@mui/material/FormControl';
import { useGridApiContext } from '@mui/x-data-grid';

import Search from 'components/Icons/Search';

import {
  ColumnMenuContainer,
  StyledOutlinedInput,
  ItemsContainer,
  StyledCheckbox,
  StyledFormControlLabel,
} from './styles';

const ColumnMenu: React.FunctionComponent<{ disableSelectAllColumns: boolean }> = ({ disableSelectAllColumns }) => {
  const context = useGridApiContext();
  const [search, setSearch] = React.useState('');

  const { lookup, columnVisibilityModel } = context.current.state.columns;

  // Match every space-separated term against both the column's label and key, so custom fields can be found by
  // their visible label (e.g. "Category of Event" matches key `events__theme`).
  const potentialColumns = Object.entries(lookup)
    .filter(([key, column]) => {
      if (!column?.headerName) return false;
      if (!search) return true;
      const haystack = `${key.replace('__', ' ')} ${column.headerName}`.toLowerCase();
      return search
        .toLowerCase()
        .trim()
        .split(' ')
        .every((term) => haystack.indexOf(term) !== -1);
    })
    .map(([key]) => [key, columnVisibilityModel[key] !== false] as [string, boolean]);

  const allSelected = potentialColumns.reduce((previous, [, current]) => current && previous, true);
  const allUnselected = potentialColumns.reduce((previous, [, current]) => !current && previous, true);

  const onSelectAllClick = (_: SyntheticEvent<Element, Event>, value: boolean): void => {
    context.current.setColumnVisibilityModel(Object.fromEntries(potentialColumns.map(([key]) => [key, value])));
  };

  return (
    <ColumnMenuContainer data-testid="grid-columns-panel">
      <FormControl fullWidth variant="outlined">
        <StyledOutlinedInput
          endAdornment={<Search />}
          fullWidth
          onChange={(event) => setSearch(event.target.value)}
          value={search}
          placeholder="Find column"
        />
      </FormControl>
      <ItemsContainer>
        {!disableSelectAllColumns && (
          <StyledFormControlLabel
            control={<StyledCheckbox checked={allSelected} indeterminate={!allSelected && !allUnselected} />}
            label="Select all"
            onChange={onSelectAllClick}
          />
        )}
        {potentialColumns.map(([key, value]) => (
          <StyledFormControlLabel
            key={key}
            control={<StyledCheckbox checked={value} />}
            label={lookup[key]?.headerName || key}
            onChange={(e) =>
              context.current.setColumnVisibility(
                key,
                (e as React.ChangeEvent<HTMLInputElement>).target.checked as boolean
              )
            }
          />
        ))}
      </ItemsContainer>
    </ColumnMenuContainer>
  );
};

export default ColumnMenu;
